import { ArcanaIdentities } from '../arcana-identities';
import { SOLA_BUSCA_IMAGE_URLS } from './sola-busca-images';
import type { DeckDefinition } from '../../../types/deck';

/** One bundled image candidate, looked up by exact file name. */
const img = (file: string) => ({ uri: SOLA_BUSCA_IMAGE_URLS[file] });

/**
 * The Sola Busca tarot — Milan/Venice, engraved in the late 15th century and
 * hand-painted by 1491. The earliest complete 78-card deck in existence and
 * the first to illustrate every pip card; its 1907 photographs in the British
 * Museum likely inspired Pamela Colman Smith. Trumps are numbered 0–XXI and
 * depict Roman and biblical figures rather than the classic allegories.
 *
 * Art: public-domain scans from the Wikimedia Commons gallery of the
 * Wikipedia article "Sola Busca tarot", bundled from
 * src/assets/images/sola-busca (see sola-busca-images.ts). Card text follows
 * the historical identifications documented in that article (Dummett, Hind,
 * Di Vincenzo).
 */
export const SOLA_BUSCA: DeckDefinition = {
    id: 'sola-busca',
    label: 'Sola Busca',
    header: {
        byline: 'The oldest tarot on Earth —',
        title: 'Sola Busca Tarot',
        slogan: 'Venice, 1491. Every card illustrated, every card alive...',
    },
    images: {
        [ArcanaIdentities.BACK]: [img('back.svg')],
        /* THELEMA is the engine's generic placeholder card — use the deck back. */
        [ArcanaIdentities.THELEMA]: [img('back.svg')],

        /* Majors — numbered 0–XXI exactly as the original deck numbers them. */
        [ArcanaIdentities.THE_FOOL]: [img('00-mato.jpg')],
        [ArcanaIdentities.THE_MAGUS]: [img('01-panfilio.jpg')],
        [ArcanaIdentities.THE_PRIESTESS]: [img('02-postumio.jpg')],
        [ArcanaIdentities.THE_EMPRESS]: [img('03-lenpio.jpg')],
        [ArcanaIdentities.THE_EMPEROR]: [img('04-mario.jpg')],
        [ArcanaIdentities.THE_HIEROPHANT]: [img('05-catulo.jpg')],
        [ArcanaIdentities.THE_LOVERS]: [img('06-sesto.jpg')],
        [ArcanaIdentities.THE_CHARIOT]: [img('07-deo-tauro.jpg')],
        [ArcanaIdentities.ADJUSTMENT]: [img('08-nerone.jpg')],
        [ArcanaIdentities.THE_HERMIT]: [img('09-falco.jpg')],
        [ArcanaIdentities.FORTUNE]: [img('10-venturio.jpg')],
        [ArcanaIdentities.LUST]: [img('11-tulio.jpg')],
        [ArcanaIdentities.THE_HANGED_MAN]: [img('12-carbone.jpg')],
        [ArcanaIdentities.DEATH]: [img('13-catone.jpg')],
        [ArcanaIdentities.ART]: [img('14-bocho.jpg')],
        [ArcanaIdentities.THE_DEVIL]: [img('15-metelo.jpg')],
        [ArcanaIdentities.THE_TOWER]: [img('16-olivo.jpg')],
        [ArcanaIdentities.THE_STAR]: [img('17-ipeo.jpg')],
        [ArcanaIdentities.THE_MOON]: [img('18-lentulo.jpg')],
        [ArcanaIdentities.THE_SUN]: [img('19-sabino.jpg')],
        [ArcanaIdentities.THE_AEON]: [img('20-nenbroto.jpg')],
        [ArcanaIdentities.THE_UNIVERSE]: [img('21-nabucho-denasor.jpg')],

        /* Cups — Coppe (Water). */
        [ArcanaIdentities.ACE_OF_CUPS]: [img('cups-ace.jpg')],
        [ArcanaIdentities.LOVE]: [img('cups-02.jpg')],
        [ArcanaIdentities.ABUNDANCE]: [img('cups-03.jpg')],
        [ArcanaIdentities.LUXURY]: [img('cups-04.jpg')],
        [ArcanaIdentities.DISAPPOINTMENT]: [img('cups-05.jpg')],
        [ArcanaIdentities.PLEASURE]: [img('cups-06.jpg')],
        [ArcanaIdentities.DEBAUCH]: [img('cups-07.jpg')],
        [ArcanaIdentities.INDOLENCE]: [img('cups-08.jpg')],
        [ArcanaIdentities.HAPPINESS]: [img('cups-09.jpg')],
        [ArcanaIdentities.SATIETY]: [img('cups-10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_CUPS]: [img('cups-knave.jpg')],
        [ArcanaIdentities.PRINCE_OF_CUPS]: [img('cups-knight.jpg')],
        [ArcanaIdentities.QUEEN_OF_CUPS]: [img('cups-queen.jpg')],
        [ArcanaIdentities.KNIGHT_OF_CUPS]: [img('cups-king.jpg')],

        /* Coins / Disks — Denari (Earth). */
        [ArcanaIdentities.ACE_OF_DISKS]: [img('coins-ace.jpg')],
        [ArcanaIdentities.CHANGE]: [img('coins-02.jpg')],
        [ArcanaIdentities.WORKS]: [img('coins-03.jpg')],
        [ArcanaIdentities.POWER]: [img('coins-04.jpg')],
        [ArcanaIdentities.WORRY]: [img('coins-05.jpg')],
        [ArcanaIdentities.SUCCESS]: [img('coins-06.jpg')],
        [ArcanaIdentities.FAILURE]: [img('coins-07.jpg')],
        [ArcanaIdentities.PRUDENCE]: [img('coins-08.jpg')],
        [ArcanaIdentities.GAIN]: [img('coins-09.jpg')],
        [ArcanaIdentities.WEALTH]: [img('coins-10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_DISKS]: [img('coins-knave.jpg')],
        [ArcanaIdentities.PRINCE_OF_DISKS]: [img('coins-knight.jpg')],
        [ArcanaIdentities.QUEEN_OF_DISKS]: [img('coins-queen.jpg')],
        [ArcanaIdentities.KNIGHT_OF_DISKS]: [img('coins-king.jpg')],

        /* Batons / Wands — Bastoni (Fire). */
        [ArcanaIdentities.ACE_OF_WANDS]: [img('batons-ace.jpg')],
        [ArcanaIdentities.DOMINION]: [img('batons-02.jpg')],
        [ArcanaIdentities.VIRTUE]: [img('batons-03.jpg')],
        [ArcanaIdentities.COMPLETION]: [img('batons-04.jpg')],
        [ArcanaIdentities.STRIFE]: [img('batons-05.jpg')],
        [ArcanaIdentities.VICTORY]: [img('batons-06.jpg')],
        [ArcanaIdentities.VALOUR]: [img('batons-07.jpg')],
        [ArcanaIdentities.SWIFTNESS]: [img('batons-08.jpg')],
        [ArcanaIdentities.STRENGTH]: [img('batons-09.jpg')],
        [ArcanaIdentities.OPPRESSION]: [img('batons-10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_WANDS]: [img('batons-knave.jpg')],
        [ArcanaIdentities.PRINCE_OF_WANDS]: [img('batons-knight.jpg')],
        [ArcanaIdentities.QUEEN_OF_WANDS]: [img('batons-queen.jpg')],
        [ArcanaIdentities.KNIGHT_OF_WANDS]: [img('batons-king.jpg')],

        /* Swords — Spade (Air). */
        [ArcanaIdentities.ACE_OF_SWORDS]: [img('swords-ace.jpg')],
        [ArcanaIdentities.PEACE]: [img('swords-02.jpg')],
        [ArcanaIdentities.SORROW]: [img('swords-03.jpg')],
        [ArcanaIdentities.TRUCE]: [img('swords-04.jpg')],
        [ArcanaIdentities.DEFEAT]: [img('swords-05.jpg')],
        [ArcanaIdentities.SCIENCE]: [img('swords-06.jpg')],
        [ArcanaIdentities.FUTILITY]: [img('swords-07.jpg')],
        [ArcanaIdentities.INTERFERENCE]: [img('swords-08.jpg')],
        [ArcanaIdentities.CRUELTY]: [img('swords-09.jpg')],
        [ArcanaIdentities.RUIN]: [img('swords-10.jpg')],
        [ArcanaIdentities.PRINCESS_OF_SWORDS]: [img('swords-knave.jpg')],
        [ArcanaIdentities.PRINCE_OF_SWORDS]: [img('swords-knight.jpg')],
        [ArcanaIdentities.QUEEN_OF_SWORDS]: [img('swords-queen.jpg')],
        [ArcanaIdentities.KNIGHT_OF_SWORDS]: [img('swords-king.jpg')],
    },
    descriptions: {
        /* ---- Majors ---- */
        THE_FOOL: {
            title: '0 · Mato — The Fool',
            body: 'Mato, the Lord of Misrule, presiding over the Feast of Fools as his Roman ancestors presided over Saturnalia: the world turned upside down by sacred licence. This is the oldest Fool in any tarot — and he stands, unambiguously, at zero. Leap: the numbering of your life has not yet begun.',
        },
        THE_MAGUS: {
            title: 'I · Panfilio — The Magician',
            body: 'Numa Pompilius, the priest-king who took the raw city of Rome and taught it its rites, its calendar and its laws. The wand of will is here a sceptre of consecration: magic as the ordering of power, not the mere wishing of it. Speak the word and build the vessel to hold it.',
        },
        THE_PRIESTESS: {
            title: 'II · Postumio — The High Priestess',
            body: 'Aulus Postumius, the dictator before whom the shining twins appeared at Lake Regillus — a general who won his battle, then told the truth about who won it. The deity in a deck of mortals: knowledge received in silence, at night, from sources that do not explain themselves. Be still, and let the oracle speak.',
        },
        THE_EMPRESS: {
            title: 'III · Lenpio — The Empress',
            body: 'Lepidus, patrician matron of the Republic in all but name — the fecund, ordering hand behind the throne. Where the Emperor decrees, she grows: harvest, household, the body politic fed from below. Tend what you have planted; it is ripening faster than you think.',
        },
        THE_EMPEROR: {
            title: 'IIII · Mario — The Emperor',
            body: 'Gaius Marius — seven times consul, Caesar\u2019s uncle, the man who rebuilt the Roman army from the ground up. Authority earned through reform and sheer staying power, not inherited. Structure your world deliberately: what you organise now will fight for you for decades.',
        },
        THE_HIEROPHANT: {
            title: 'V · Catulo — The Hierophant',
            body: 'Quintus Lutatius Catulus, who rebuilt the Capitol with his own hands when it burned — devotion to the temple, not the temple of devotion. Tradition as something maintained by hands, renewed by choice. Take the teaching; keep what is living in it.',
        },
        THE_LOVERS: {
            title: 'VI · Sesto — The Lovers',
            body: 'Sextus Tarquinius, whose violation of Lucretia broke the kings and founded the Republic — the deck\u2019s reminder that desire is never private for long. Love, choice and consequence in one figure. Choose knowingly: what you unite with now will name your future.',
        },
        THE_CHARIOT: {
            title: 'VII · Deo Tauro — The Chariot',
            body: 'Deiotarus, the bull-king of Galatia, riding the chariot of Zeus — the god himself as the bull that carries off Europa. Motion powered by something divine and only half-tamed. Hold the reins and enjoy the ride: momentum is yours if you do not fight the animal.',
        },
        ADJUSTMENT: {
            title: 'VIII · Nerone — Strength',
            body: 'The child Nero, gripping in small fists what other decks give as a lion\u2019s jaws — strength in its most chilling, unripened form. This is the position the Rider deck borrowed: Fortitude at eight. Your power is real; whether it ripens into courage or cruelty depends on the hand that holds it.',
        },
        THE_HERMIT: {
            title: 'VIIII · Falco — The Hermit',
            body: 'The falconer, crowned and armoured, kneeling alone with his hooded bird — a hermit of the noble hunt, withdrawn from court with only the hawk for company. Solitude here is not poverty but discipline: the taming of a wild thing between gloved hands. Go apart, and train what is wild in you.',
        },
        FORTUNE: {
            title: 'X · Venturio — Wheel of Fortune',
            body: 'Venturio walking on winged sandals, the eternal traveller the wheel carries wherever it wills — fortune\u2019s courier, never fortune\u2019s master. The oldest Wheel in tarot, and it turns the same: what rises falls, what falls rises. Ride the turn; do not argue with it.',
        },
        LUST: {
            title: 'XI · Tulio — Justice',
            body: 'Tullius Cicero, torch ablaze and sword at his hip — the tongue that condemned Catiline, the force of truth spoken aloud in a hostile Senate. The deck keeps Justice here, as the Rider deck keeps Strength at eight: two older virtues, swapped. Measure, decide, and mean it; your word is the sword.',
        },
        THE_HANGED_MAN: {
            title: 'XII · Carbone — The Hanged Man',
            body: 'Papirius Carbo, three times consul and finally a traitor to the Republic he served — the Italian Hanged Man is simply called the Traditore. He walks bent beneath his lamp and staff, Rome\u2019s quiver on his back, under a crescent moon. Suspension, reversal, the view from the underside of your own life: see what the fall shows you.',
        },
        DEATH: {
            title: 'XIII · Catone — Death',
            body: 'Cato of Utica, who chose death over a life under Caesar — the only card in the deck that shows a corpse, and it wears the most honourable face in Rome. Endings chosen rather than suffered; the clean cut that preserves integrity. Let what is finished be finished.',
        },
        ART: {
            title: 'XIIII · Bocho — Temperance',
            body: 'Bocchus, king of Mauretania, eyes raised to heaven as he weighs the decision that would make him history\u2019s arch-traitor — armour embraced, helmet set aside, every option burning. The alchemical middle path is a tightrope walked between two loyalities. Blend your opposites deliberately; the mixture is the art.',
        },
        THE_DEVIL: {
            title: 'XV · Metelo — The Devil',
            body: 'Metellus, hero of a burning temple, seated beside the pillar that carries the Palladium — the sacred image he saved, now the image he serves. The oldest Devil in tarot is not a monster but an idolatry: the thing you rescued becoming the thing that rules you. Name your chains; you picked them.',
        },
        THE_TOWER: {
            title: 'XVI · Olivo — The Tower',
            body: 'The lightning-struck olive — the Tower rendered as a sacred tree, split by fire from a clear sky. Livy\u2019s name has grown into the card the way the tree grew into the bolt. What is struck was already hollow; let the ruin reveal it, and keep the roots.',
        },
        THE_STAR: {
            title: 'XVII · Ipeo — The Star',
            body: 'Ipeo, winged and hooded, praying before the radiant head that blooms from a dead, serpent-coiled tree — hope flowering precisely out of ruin, with the old demon Ipos hiding in the name. After the Tower\u2019s fall, the light returns from an impossible place. Offer your intention to it and walk on.',
        },
        THE_MOON: {
            title: 'XVIII · Lentulo — The Moon',
            body: 'Lentulus, the old priest keeping the flame alive on its twisted pillar while the wheel of night turns below — a lantern against the long dark. The moon\u2019s country is dream, doubt and the light that is only borrowed. Walk slowly between the towers of sleep; not every voice in the dark is yours.',
        },
        THE_SUN: {
            title: 'XVIIII · Sabino — The Sun',
            body: 'Masurius Sabinus, the great jurist, standing in the full armour of daylight with the rayed sun-disc in his fist. Clarity as an achievement of law: warmth, success and the mind made bright. Come out of shadow; everything is simpler than the night insisted.',
        },
        THE_AEON: {
            title: 'XX · Nenbroto — Judgement',
            body: 'Nimrod — Nenbroto — builder of the Tower of Babel, standing beneath the moment heaven answered. The oldest Judgement in tarot is a warning and a promise at once: the call does sound, and it reorders everything that pretended to be high. Rise when you are called; leave the tower where it fell.',
        },
        THE_UNIVERSE: {
            title: 'XXI · Nabucho-denasor — The World',
            body: 'Nebuchadnezzar before the T-and-O map of creation, the whole world drawn flat with its dragons waiting at the edges. The deck\u2019s final card shows mastery as comprehension: the pattern seen entire, the dance complete. You hold the map; walk the world with both.',
        },

        /* ---- Cups · Coppe ---- */
        ACE_OF_CUPS: {
            title: 'Ace of Cups · Coppe 1',
            body: 'The engraved chalice of the oldest illustrated deck in the world — feeling in its first, unspilled form. Among the Sola Busca pips the suit of Coppe carries love, appetite and the soul\u2019s water. Let it be poured; the vessel is new.',
        },
        LOVE: {
            title: 'Two of Cups · Coppe 2',
            body: 'Venus in Cancer. Attraction recognised and answered — the first true mirroring of two natures. In the oldest deck as in the newest, this is the card of the bond begun. Meet it honestly.',
        },
        ABUNDANCE: {
            title: 'Three of Cups · Coppe 3',
            body: 'Mercury in Cancer. Feeling multiplied and shared — friendship, harvest, the table with too many cups and no cause to count them. Joy grows by being witnessed. Invite the witnesses.',
        },
        LUXURY: {
            title: 'Four of Cups · Coppe 4',
            body: 'Moon in Cancer. Comfort deepening toward saturation — tenderness with a faint edge of boredom. The old deck\u2019s warning about too much sweetness. Enjoy the softness; watch the door.',
        },
        DISAPPOINTMENT: {
            title: 'Five of Cups · Coppe 5',
            body: 'Mars in Scorpio. The cup lost — affection broken on the stone of expectation. Five hundred years before the Rider deck\u2019s spilled chalice, this water ran the same way. Grieve cleanly; the rest still holds.',
        },
        PLEASURE: {
            title: 'Six of Cups · Coppe 6',
            body: 'Sun in Scorpio. Simple delight — the past returning as warmth rather than debt. The engraved pips remind us pleasure needed no excuse in 1491 either. Take it as offered.',
        },
        DEBAUCH: {
            title: 'Seven of Cups · Coppe 7',
            body: 'Venus in Scorpio. Desire drinking past its own edge — the heady blur where feeling and fantasy trade places. Venice\u2019s deck knows this card is also the artist\u2019s door. Drink from the vision; do not move in.',
        },
        INDOLENCE: {
            title: 'Eight of Cups · Coppe 8',
            body: 'Saturn in Pisces. Feeling stalled — the cup set down mid-taste, the heart drifting short of its own goal. The oldest deck names no villain here, only the fog. Return to the work, or release it whole.',
        },
        HAPPINESS: {
            title: 'Nine of Cups · Coppe 9',
            body: 'Jupiter in Pisces. The wish made warm and real — emotional completion, contentment with witnesses. Some cards have been lucky for five centuries; this is one. Accept without negotiation.',
        },
        SATIETY: {
            title: 'Ten of Cups · Coppe 10',
            body: 'Mars in Pisces. The heart\u2019s work completed — love that has filled every vessel and asks only to be kept. Fullness is not an ending but a hearth. Rest here, then begin again.',
        },
        PRINCESS_OF_CUPS: {
            title: 'Knave of Cups · Fante di Coppe',
            body: 'Earth of Water. The young page of the soul\u2019s water — feeling in its apprentice form: tender, dreamy, half in love with the idea before the fact. Trust the small still voice; it is learning to speak true.',
        },
        PRINCE_OF_CUPS: {
            title: 'Knight of Cups · Cavallo di Coppe — Natanabo',
            body: 'Air of Water. Natanabo — Nectanebo II, the last pharaoh, remembered here as the Egyptian priest and magician. Feeling governed by an adept\u2019s mind: subtle, romantic, quietly calculating depths. What he offers, he offers deliberately.',
        },
        QUEEN_OF_CUPS: {
            title: 'Queen of Cups · Regina di Coppe — Polisena',
            body: 'Water of Water. Polisena — Polyxena, the Trojan princess whose name history gave to Alexander\u2019s sorceress mother. The suit\u2019s depth personified: empathic, psychically open, keeping her own shape inside every current she mirrors.',
        },
        KNIGHT_OF_CUPS: {
            title: 'King of Cups · Re di Coppe — Lucio Cecilio',
            body: 'Fire of Water. Lucius Caecilius, the cup\u2019s crowned master — maturity of feeling, love that has learned stewardship. The grail borne steadily by a seated king. Follow him for beauty; the answer is not his to command.',
        },

        /* ---- Coins · Denari ---- */
        ACE_OF_DISKS: {
            title: 'Ace of Coins · Denari 1',
            body: 'The first coin of the first fully-illustrated deck — matter in its seed form, the tangible beginning. The suit of Denari carries money, body and the works of hands. Plant something real.',
        },
        CHANGE: {
            title: 'Two of Coins · Denari 2',
            body: 'Jupiter in Capricorn. Matter kept in play — two weights juggled by an unhurried hand. The engraver\u2019s line and the accountant\u2019s ledger agree: the skill is the rhythm. Keep both plates spinning.',
        },
        WORKS: {
            title: 'Three of Coins · Denari 3',
            body: 'Mars in Capricorn. Labour made visible — the designed thing rising from coordinated hands. Five centuries of craftsmen have read this card over their benches. Build it properly; it will outlast the building.',
        },
        POWER: {
            title: 'Four of Coins · Denari 4',
            body: 'Sun in Capricorn. The cornerstone set — material stability, control grounded in substance. A fortress is only useful if it has a gate. Hold it open-handedly.',
        },
        WORRY: {
            title: 'Five of Coins · Denari 5',
            body: 'Mercury in Taurus. The mind gnawing on matter — material anxiety, the counting-house at three in the morning. The coin looms larger than the harvest it could buy. Restore the light; the engine quiets.',
        },
        SUCCESS: {
            title: 'Six of Coins · Denari 6',
            body: 'Moon in Taurus. Generous harvest — achievement made sweeter by the open hand. The old merchant-cities knew this arithmetic: what flows from real plenty returns as real standing. Share the purse.',
        },
        FAILURE: {
            title: 'Seven of Coins · Denari 7',
            body: 'Saturn in Taurus. Effort that never ripened — the harvest failed, not the field. In the deck that invented illustrated pips, this is postponement, not verdict. Reassess before pressing on.',
        },
        PRUDENCE: {
            title: 'Eight of Coins · Denari 8',
            body: 'Sun in Virgo. The careful hand — skill, patience, attention to the material fact. Prudence is the craftsman\u2019s virtue, and this whole deck is its monument. Pay in daily; mastery is compound interest.',
        },
        GAIN: {
            title: 'Nine of Coins · Denari 9',
            body: 'Venus in Virgo. Increase quietly earned — fruit on the tree you planted while attention was on the work. Inheritance, earnings, the vineyard that is yours. Walk it; it is enough.',
        },
        WEALTH: {
            title: 'Ten of Coins · Denari 10',
            body: 'Mercury in Virgo. The harvest stored — the cycle closed in plenty, the house provided for. Wealth as the engraver drew it: the granary full at the door of winter. Guard it as a well, not a wall.',
        },
        PRINCESS_OF_DISKS: {
            title: 'Knave of Coins · Fante di Denari',
            body: 'Earth of Earth. Matter in its purest patience — the young page of the coin suit, practical beyond her years. Growth as a way of being. Everything she tends, lives.',
        },
        PRINCE_OF_DISKS: {
            title: 'Knight of Coins · Cavallo di Denari — Sarafino',
            body: 'Air of Earth. Sarafino — Serapis, the Graeco-Egyptian lord of the underworld\u2019s riches, riding the coin suit\u2019s mounted rank. Methodical, reliable, quietly stubborn. Others outrun him to everything except the finish line.',
        },
        QUEEN_OF_DISKS: {
            title: 'Queen of Coins · Regina di Denari — Elena',
            body: 'Water of Earth. Elena — Helen, the face that launched a fleet, seated here on the suit of tangible things. Resourceful, warm, magnetic; she makes everything she touches grow. Prosperity follows her like a season.',
        },
        KNIGHT_OF_DISKS: {
            title: 'King of Coins · Re di Denari — R. Filipo',
            body: 'Fire of Earth. Philip — R. Filipo, King of Macedon, father of conquerors, crowned master of the material realm. Endurance and provision as kingship: the slow fire that cannot be extinguished, only finished.',
        },

        /* ---- Batons · Bastoni ---- */
        ACE_OF_WANDS: {
            title: 'Ace of Batons · Bastoni 1',
            body: 'The first staff of the oldest deck — will in its raw sprouting form. The suit of Bastoni carries fire, enterprise and the creative stroke. The spark this pure becomes either hearth or wildfire; plant it deliberately.',
        },
        DOMINION: {
            title: 'Two of Batons · Bastoni 2',
            body: 'Mars in Aries. The first bold stroke of will upon the world — authority asserted without apology. The engraved staff raised high means what it has always meant. Act now; this energy does not store.',
        },
        VIRTUE: {
            title: 'Three of Batons · Bastoni 3',
            body: 'Sun in Aries. Will confirmed by its own success — strength born of alignment. The ships have sailed and the horizon is opening. Keep creating in the same true direction.',
        },
        COMPLETION: {
            title: 'Four of Batons · Bastoni 4',
            body: 'Venus in Aries. A work of fire perfected and celebrated — the harvest of discipline, enjoyed without guilt. Every deck since agrees: rest is part of the building. Let this be a genuine resting point.',
        },
        STRIFE: {
            title: 'Five of Batons · Bastoni 5',
            body: 'Saturn in Leo. Competing fires locked in contest — ambition against ambition, staff against staff. The friction is the examination, not the failure. Fight cleanly and learn what you actually want.',
        },
        VICTORY: {
            title: 'Six of Batons · Bastoni 6',
            body: 'Jupiter in Leo. Triumph after genuine struggle — success earned, not inherited. The laurel is five centuries old and still fits. Regard what bought it; wear it outwardly as generosity.',
        },
        VALOUR: {
            title: 'Seven of Batons · Bastoni 7',
            body: 'Mars in Leo. Courage under sustained pressure — defiance as a form of grace. The staff held against odds is this deck\u2019s oldest posture. Hold the line; the odds are real and so is the strength.',
        },
        SWIFTNESS: {
            title: 'Eight of Batons · Bastoni 8',
            body: 'Mercury in Sagittarius. Energy unbound — the eight staves in flight, events accelerating beyond planning. The oldest deck drew the arrow too. Steer on the wing; precision now comes from timing.',
        },
        STRENGTH: {
            title: 'Nine of Batons · Bastoni 9',
            body: 'Moon in Sagittarius. Immense force held in conscious reserve — preparedness, resilience, the bandaged guard who never left his post. Do not mistake restraint for weakness; it is the flame that outlasts the night.',
        },
        OPPRESSION: {
            title: 'Ten of Batons · Bastoni 10',
            body: 'Saturn in Sagittarius. Will turned tyrant — force crushing what it meant to carry. The overloaded figure bent beneath his own staves is five hundred years old and still at work. Put the burden down and ask what it was for.',
        },
        PRINCESS_OF_WANDS: {
            title: 'Knave of Batons · Fante di Bastoni',
            body: 'Earth of Fire. The spark incarnate — fire finding a foothold in the real world, impulsive, adventurous, magnetic. Her gift is beginnings; her lesson is that a spark must become a hearth.',
        },
        PRINCE_OF_WANDS: {
            title: 'Knight of Batons · Cavallo di Bastoni — Apolino',
            body: 'Air of Fire. Apolino — Apollo, or the poet Apollonius who wrote the Argonauts\u2019 voyage, riding the fire suit\u2019s swift rank. The charioteer of will: decisive, intense, dramatic. His flame illuminates or consumes, depending on whether thought touches the reins.',
        },
        QUEEN_OF_WANDS: {
            title: 'Queen of Batons · Regina di Bastoni — Palas',
            body: 'Water of Fire. Palas — Pallas Athena, the armed wisdom who was Rome\u2019s own Palladium, enthroned on the suit of fire. Charisma with a spear behind it: utterly self-assured, lighting every flame she warms. The strongest fire is the one that lights others.',
        },
        KNIGHT_OF_WANDS: {
            title: 'King of Batons · Re di Bastoni — Levio Plauto',
            body: 'Fire of Fire. Titus Maccius Plautus — Rome\u2019s great comedian crowned as the staff-suit\u2019s king, the fire of creation that never takes itself wholly seriously. Daring, magnificent, total in commitment. Follow him for the beginning, never for the follow-through.',
        },

        /* ---- Swords · Spade ---- */
        ACE_OF_SWORDS: {
            title: 'Ace of Swords · Spade 1',
            body: 'The first blade of the oldest deck — the mind in its first cut. The suit of Spade carries intellect, conflict and truth sharp enough to divide. Take up the sword knowingly: what it severs cannot be rejoined.',
        },
        PEACE: {
            title: 'Two of Swords · Spade 2',
            body: 'Moon in Libra. The blade sheathed — the mind at rest in perfect equilibrium, nothing yet decided and nothing yet lost. Guard the balance by deciding nothing until the moon is full.',
        },
        SORROW: {
            title: 'Three of Swords · Spade 3',
            body: 'Saturn in Libra. The heart pierced by clear sight — the card the Rider deck borrowed almost whole from this engraving. Truth arriving where it hurts. Let it cut cleanly; a clean wound heals true.',
        },
        TRUCE: {
            title: 'Four of Swords · Spade 4',
            body: 'Jupiter in Libra. Fragile concord — rest from conflict without resolution of it, the blades laid down in pause rather than peace. Use the interval to strengthen; peace held lightly is peace kept.',
        },
        DEFEAT: {
            title: 'Five of Swords · Spade 5',
            body: 'Venus in Aquarius. The mind turned against itself — quarrel, collapse, the strange hollow after contest. What breaks is the frame, not the fighter. Withdraw with what dignity remains.',
        },
        SCIENCE: {
            title: 'Six of Swords · Spade 6',
            body: 'Mercury in Aquarius. The perfect instrument — method, objectivity, exact thought moving away from trouble without denial. Problems here yield to precision, not force. Cross the water; the mind knows the way.',
        },
        FUTILITY: {
            title: 'Seven of Swords · Spade 7',
            body: 'Moon in Aquarius. Unsteady effort — ingenuity without stamina, the clever manoeuvre that dissolves by dawn. The oldest deck has no patience for the half-plan. Stop pushing; reopen the windows.',
        },
        INTERFERENCE: {
            title: 'Eight of Swords · Spade 8',
            body: 'Saturn in Gemini. Force scattered — too many blades, not one blow; distraction crowding the narrow way. Prune. A single edge will cut what eight cannot touch.',
        },
        CRUELTY: {
            title: 'Nine of Swords · Spade 9',
            body: 'Mars in Gemini. The mind at war with itself — the sleepless interrogation that calls itself honesty. The mercy is waking; the swords that torture are also swords that can be dropped.',
        },
        RUIN: {
            title: 'Ten of Swords · Spade 10',
            body: 'Sun in Gemini. The edifice of thought collapsed — the figure brought low beneath ten blades, the image Pamela Smith carried straight into the modern deck. Intellectual death clears ground. Nothing grows on dishonest foundations.',
        },
        PRINCESS_OF_SWORDS: {
            title: 'Knave of Swords · Fante di Spade',
            body: 'Earth of Air. Thought landing in the real — the vigilant young page of the blade suit, cutting through comfort to fact without malice. Her gift is the clean perception others avoid; her lesson is when to sheathe it.',
        },
        PRINCE_OF_SWORDS: {
            title: 'Knight of Swords · Cavallo di Spade — Amone',
            body: 'Air of Air. Amone — Zeus Ammon, the horned oracle-god, putative father of Alexander, worshipped at Siwah. Intellect as element: brilliant, versatile, ruthless in logic. What he cannot cut, he does not believe.',
        },
        QUEEN_OF_SWORDS: {
            title: 'Queen of Swords · Regina di Spade — Olinpia',
            body: 'Water of Air. Olinpia — Olympias, Alexander\u2019s mother, the sorceress of Epirus who kept snakes and counselled kings. Clear-eyed, independent, unbowed; her solitude became scale. She extends the sword as justice: precisely, and to exactly the length required.',
        },
        KNIGHT_OF_SWORDS: {
            title: 'King of Swords · Re di Spade — Alecxandro M.',
            body: 'Fire of Air. Alecxandro Magno — Alexander the Great, whom the medieval Secret of Secrets initiated into alchemy by his master Aristotle. The deck\u2019s crowned summit: intellect weaponised and released, right more often than kind. Deploy him where truth must arrive on time.',
        },
    },
};
